import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { getEmbedHeaders } from "../context/EmbedContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const BACKEND_STREAM_URL = `${BACKEND_URL}/api/v1/chat`;
const ENABLE_STRUCTURED_MODE = process.env.REACT_APP_ENABLE_STRUCTURED_MODE === "true";

const extractSseEvents = (buffer) => {
    const events = [];
    let working = buffer;
    let splitAt = working.indexOf("\n\n");
    while (splitAt !== -1) {
        const rawEvent = working.slice(0, splitAt).replace(/\r/g, "");
        working = working.slice(splitAt + 2);
        splitAt = working.indexOf("\n\n");
        if (!rawEvent.trim()) continue;

        const dataLines = rawEvent
            .split("\n")
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.replace(/^data:\s?/, ""));
        if (!dataLines.length) continue;

        const payload = dataLines.join("\n");
        try {
            events.push(JSON.parse(payload));
        } catch {
            working = `${rawEvent}\n\n${working}`;
            break;
        }
    }
    return { events, remaining: working };
};

/**
 * Custom hook encapsulating all chat/SSE streaming logic.
 *
 * @param {Object} deps – external dependencies (no Clerk / router objects directly)
 * @param {Function} deps.getToken        – returns a Bearer token (async)
 * @param {boolean}  deps.isSignedIn      – whether the current user is signed in
 * @param {Function} deps.openSignIn      – opens the sign-in modal
 * @param {string|null} deps.currentConversationId
 * @param {Function} deps.fetchConversations
 * @param {boolean}  deps.hasSubscription
 * @param {boolean}  deps.trialExpired
 * @param {Function} deps.setShowPaywall
 * @param {boolean}  deps.embedMode       – embed iframe: no Clerk, X-Embed-Mode header
 */
export default function useChat({
    getToken,
    isSignedIn,
    openSignIn,
    currentConversationId,
    fetchConversations,
    hasSubscription,
    trialExpired,
    setShowPaywall,
    embedMode = false,
}) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [sessionId, setSessionId] = useState("");
    const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
    const [isStructuredMode, setIsStructuredMode] = useState(false);
    const [connectionState, setConnectionState] = useState("idle");

    const queryClient = useQueryClient();

    const abortControllerRef = useRef(null);
    const lastUserPromptRef = useRef("");
    const messagesEndRef = useRef(null);

    // ---- Scroll to bottom on new messages ----
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    // ---- Initial greeting ----
    useEffect(() => {
        if (!currentConversationId && messages.length === 0) {
            setSessionId(
                `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            );
            setMessages([
                {
                    sender: "bot",
                    content:
                        'Hello! I can help you with digital accessibility compliance and testing.\nTry asking something like: "How do I check if my website meets WCAG 2.1 AA standards?" or "What are the common Section 508 compliance issues?"',
                },
            ]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- Helpers exposed to consumers ----
    const handleNewChat = () => {
        setSessionId(
            `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        );
        setMessages([
            {
                sender: "bot",
                content:
                    'Hello! I can help you with digital accessibility compliance and testing.\nTry asking something like: "How do I check if my website meets WCAG 2.1 AA standards?" or "What are the common Section 508 compliance issues?"',
            },
        ]);
    };

    const handleStopGenerating = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    };

    const handleRegenerate = async () => {
        const lastUserMessage = [...messages].reverse().find((m) => m.sender === "user");
        if (!lastUserMessage) return;
        await sendMessage({ overrideText: lastUserMessage.content, appendUserMessage: false });
    };

    const sendMessage = async ({ overrideText = null, appendUserMessage = true } = {}) => {
        const rawInput = overrideText ?? input;
        if (!rawInput || !rawInput.trim()) return;
        if (!embedMode && !isSignedIn) {
            openSignIn();
            return;
        }

        const sanitizedInput = DOMPurify.sanitize(rawInput.trim());
        lastUserPromptRef.current = sanitizedInput;

        if (appendUserMessage) {
            setMessages((prev) => [...prev, { sender: "user", content: sanitizedInput }]);
            setInput("");
        }

        setIsTyping(true);
        setConnectionState("connecting");
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const token = embedMode ? null : await getToken();
            const authHeaders = embedMode
                ? getEmbedHeaders()
                : { Authorization: `Bearer ${token}` };

            if (ENABLE_STRUCTURED_MODE && isStructuredMode) {
                const structuredResp = await fetch(`${BACKEND_URL}/api/v1/chat/structured`, {
                    method: "POST",
                    headers: {
                        ...authHeaders,
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    signal: controller.signal,
                    body: JSON.stringify({
                        question: sanitizedInput,
                        schema: {
                            type: "object",
                            properties: {
                                summary: { type: "string" },
                                key_points: { type: "array", items: { type: "string" } },
                                actions: { type: "array", items: { type: "string" } },
                            },
                            required: ["summary", "key_points", "actions"],
                            additionalProperties: false,
                        },
                    }),
                });

                if (structuredResp.status === 402 || structuredResp.status === 403) {
                    setShowPaywall(true);
                    setIsTyping(false);
                    setConnectionState("idle");
                    return;
                }
                const structuredData = await structuredResp.json();
                if (!structuredResp.ok) {
                    throw new Error(structuredData.message || "Structured output request failed.");
                }
                setMessages((prev) => [
                    ...prev,
                    {
                        sender: "bot",
                        content: structuredData.outputText || "Structured response generated.",
                        structuredData: structuredData.data || {},
                    },
                ]);
                setIsTyping(false);
                setConnectionState("idle");
                if (!embedMode && !currentConversationId) {
                    queryClient.invalidateQueries({ queryKey: ['conversations'] });
                }
                return;
            }

            const response = await fetch(BACKEND_STREAM_URL, {
                method: "POST",
                headers: {
                    ...authHeaders,
                    "Content-Type": "application/json",
                },
                credentials: "include",
                signal: controller.signal,
                body: JSON.stringify({
                    question: sanitizedInput,
                    session_id: sessionId,
                }),
            });

            if (response.status === 402 || response.status === 403) {
                setShowPaywall(true);
                setIsTyping(false);
                setConnectionState("idle");
                return;
            }
            if (!response.ok || !response.body) {
                throw new Error(`Streaming request failed (${response.status})`);
            }

            setConnectionState("streaming");
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let botReply = "";
            let pending = "";
            let sources = [];
            let gotDone = false;
            let gotError = false;
            let finalErrorMessage = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                pending += decoder.decode(value, { stream: true });
                const { events, remaining } = extractSseEvents(pending);
                pending = remaining;

                for (const data of events) {
                    if (data.type === "chunk") {
                        botReply += data.content || "";
                        const replySnapshot = botReply;
                        const sourcesSnapshot = [...sources];
                        setMessages((prev) => {
                            const next = [...prev];
                            const last = next[next.length - 1];
                            if (last?.sender === "bot_typing") {
                                last.content = replySnapshot;
                                last.sources = sourcesSnapshot;
                            } else {
                                next.push({
                                    sender: "bot_typing",
                                    content: replySnapshot,
                                    sources: sourcesSnapshot,
                                });
                            }
                            return next;
                        });
                    } else if (data.type === "sources") {
                        sources = Array.isArray(data.sources) ? data.sources : [];
                        const sourcesSnapshot = [...sources];
                        setMessages((prev) => {
                            const next = [...prev];
                            const last = next[next.length - 1];
                            if (last?.sender === "bot_typing") {
                                last.sources = sourcesSnapshot;
                            }
                            return next;
                        });
                    } else if (data.type === "done") {
                        gotDone = true;
                        setIsTyping(false);
                        setConnectionState("idle");
                        setMessages((prev) =>
                            prev.map((m) =>
                                m.sender === "bot_typing"
                                    ? { sender: "bot", content: m.content, sources: m.sources || [] }
                                    : m
                            )
                        );
                        if (!embedMode && !currentConversationId) {
                            queryClient.invalidateQueries({ queryKey: ['conversations'] });
                        }
                    } else if (data.type === "error") {
                        gotError = true;
                        setIsTyping(false);
                        setConnectionState("error");
                        setMessages((prev) => [...prev, { sender: "bot", content: `Error: ${data.content}` }]);
                    }
                }

                // Break out of reader loop if the server signaled it's done or errored
                if (gotDone || gotError) break;
            }

            if (!gotDone && !gotError && pending.trim()) {
                const finalParsed = extractSseEvents(`${pending}\n\n`);
                for (const data of finalParsed.events) {
                    if (data.type === "done") {
                        gotDone = true;
                    } else if (data.type === "error") {
                        gotError = true;
                        finalErrorMessage = data.content || "";
                    }
                }
            }

            if (gotDone) {
                setIsTyping(false);
                setConnectionState("idle");
                setMessages((prev) =>
                    prev.map((m) =>
                        m.sender === "bot_typing"
                            ? { sender: "bot", content: m.content, sources: m.sources || [] }
                            : m
                    )
                );
                if (!embedMode && !currentConversationId) {
                    queryClient.invalidateQueries({ queryKey: ['conversations'] });
                }
            }

            if (gotError) {
                setIsTyping(false);
                setConnectionState("error");
                if (finalErrorMessage) {
                    setMessages((prev) => [...prev, { sender: "bot", content: `Error: ${finalErrorMessage}` }]);
                }
            }

            if (!gotDone && !gotError && botReply) {
                setIsTyping(false);
                setConnectionState("idle");
                setMessages((prev) =>
                    prev.map((m) =>
                        m.sender === "bot_typing"
                            ? { sender: "bot", content: m.content, sources: m.sources || [] }
                            : m
                    )
                );
            }
        } catch (error) {
            if (error.name === "AbortError") {
                setMessages((prev) => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last?.sender === "bot_typing") {
                        last.sender = "bot";
                        last.content = `${last.content}\n\n_Stopped by user._`;
                    }
                    return next;
                });
            } else {
                console.error("Fetch stream error:", error);
                setMessages((prev) => [
                    ...prev,
                    { sender: "bot", content: "Connection error. Please try again." },
                ]);
                setConnectionState("error");
            }
            setIsTyping(false);
        } finally {
            abortControllerRef.current = null;
            setConnectionState((prev) => (prev === "error" ? "error" : "idle"));
        }
    };

    const copyToClipboard = async (text, messageIndex) => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                setCopiedMessageIndex(messageIndex);
            } else {
                throw new Error("Clipboard API not available");
            }
        } catch (err) {
            console.warn("Clipboard API failed, trying fallback:", err);
            try {
                const textArea = document.createElement("textarea");
                textArea.value = text;
                textArea.style.position = "fixed";
                textArea.style.left = "-9999px";
                textArea.style.top = "0";
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const successful = document.execCommand("copy");
                document.body.removeChild(textArea);
                if (successful) {
                    setCopiedMessageIndex(messageIndex);
                }
            } catch (fallbackErr) {
                console.error("Failed to copy text:", fallbackErr);
            }
        }

        if (messageIndex !== null) {
            setTimeout(() => {
                setCopiedMessageIndex(null);
            }, 2000);
        }
    };

    const renderStructuredBlock = (data) => {
        if (!data || typeof data !== "object") return null;
        return (
            <div className="structured-block">
                {Object.entries(data).map(([key, value]) => (
                    <div key={key} className="structured-row">
                        <div className="structured-key">{key}</div>
                        <div className="structured-value">
                            {Array.isArray(value) ? value.join(", ") : String(value)}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return {
        // State
        messages,
        setMessages,
        input,
        setInput,
        isTyping,
        sessionId,
        setSessionId,
        copiedMessageIndex,
        isStructuredMode,
        setIsStructuredMode,
        connectionState,

        // Refs
        messagesEndRef,

        // Actions
        sendMessage,
        handleStopGenerating,
        handleRegenerate,
        handleNewChat,
        copyToClipboard,

        // Render helpers
        renderStructuredBlock,

        // Constants
        ENABLE_STRUCTURED_MODE,
    };
}
